<template>
  <section class="base-timer" :class="{
    fontLoaded,
    hidden: !(showTimer || votingComplete)
  }">
    <svg v-if="showTimer && !isTimerEnded && !votingComplete && scoring === 'pointsAndTiming'" class="base-timer__svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <g class="base-timer__circle">
        <circle class="base-timer__path-elapsed" cx="50" cy="50" r="45"></circle>
        <path
          :stroke-dasharray="circleDasharray"
          class="base-timer__path-remaining"
          :class="remainingPathColor"
          d="
            M 50, 50
            m -45, 0
            a 45,45 0 1,0 90,0
            a 45,45 0 1,0 -90,0
          "
        ></path>
      </g>
    </svg>
    <span v-if="showTimer && !isTimerEnded && !votingComplete && scoring === 'pointsAndTiming'" class="base-timer__label">{{ formattedTimeLeft }}</span>
    <span v-if="showTimer && isTimerEnded && !votingComplete && scoring === 'pointsAndTiming'" class="base-timer__times-up">Time's up!</span>
    <ThankYou v-if="votingComplete" />
  </section>
</template>

<script>
import {
  mapState,
  mapMutations,
  mapGetters,
} from 'vuex';

import ThankYou from './ThankYou';

const FULL_DASH_ARRAY = 283;
const WARNING_THRESHOLD = 10;
const ALERT_THRESHOLD = 5;

const COLOR_CODES = {
  info: {
    color: 'green'
  },
  warning: {
    color: 'orange',
    threshold: WARNING_THRESHOLD
  },
  alert: {
    color: 'red',
    threshold: ALERT_THRESHOLD
  }
};

export default {
  name: 'Timer',
  components: {
    ThankYou
  },
  data() {
    return {
      timePassed: 0,
      timerInterval: null
    };
  },
  computed: {
    ...mapState('poll', [
      'votingComplete',
      'fontLoaded',
      'pollId',
      'oldPollId',
      'isTimerEnded',
    ]),
    ...mapState('voter', [
      'showTimer',
      'timerLimit',
    ]),
    ...mapState('pollGroup', ['scoring']),
    circleDasharray() {
      return `${(this.timeFraction * FULL_DASH_ARRAY).toFixed(0)} 283`;
    },
    formattedTimeLeft() {
      const timeLeft = this.timeLeft;
      const minutes = Math.floor(timeLeft / 60);
      const seconds = `${timeLeft % 60 < 10 ? 0 : ''}${timeLeft % 60}`;
      return `${minutes}:${seconds}`;
    },
    timeLeft() {
      return this.timerLimit - this.timePassed;
    },
    timeFraction() {
      const rawTimeFraction = this.timeLeft / this.timerLimit;
      return rawTimeFraction - (1 / this.timerLimit) * (1 - rawTimeFraction);
    },
    remainingPathColor() {
      const { alert, warning, info } = COLOR_CODES;

      if (this.timeLeft <= alert.threshold) {
        return alert.color;
      } else if (this.timeLeft <= warning.threshold) {
        return warning.color;
      } else {
        return info.color;
      }
    }
  },
  watch: {
    pollId(newValue) {
      if (newValue !== this.oldPollId) {
        this.resetStart();
      }
    },
    showTimer(newValue) {
      if (newValue && !this.votingComplete) {
        this.startTimer();
      } else {
        this.abortTimer();
      }
    },
    votingComplete(newValue) {
      newValue ? this.abortTimer() : this.resetStart();
    },
    timeLeft(newValue) {
      (newValue === 0) && this.onTimesUp();
    }
  },
  mounted() {
    this.showTimer && this.startTimer();
  },
  beforeDestroy() {
    this.abortTimer()
  },
  methods: {
    ...mapMutations('poll', [
      'disableVoting',
      'endTimer',
    ]),
    abortTimer() {
      clearInterval(this.timerInterval);
    },
    onTimesUp() {
      this.abortTimer();
      this.disableVoting();
      this.endTimer();
    },
    startTimer() {
      this.timerInterval = setInterval(() => (this.timePassed += 1), 1000);
    },
    resetStart() {
      this.abortTimer();
      if (this.showTimer) {
        this.timePassed = 0;
        this.startTimer();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.base-timer {
  flex: 0 0 auto;
  position: relative;
  width: 54px;
  height: 54px;
  margin: 16px auto 0;

  transition:
    margin 750ms cubic-bezier(0.22, 1, 0.36, 1),
    height 750ms cubic-bezier(0.22, 1, 0.36, 1);

  &__svg {
    transform: scaleX(-1);
  }

  &__circle {
    fill: none;
    stroke: none;
  }

  &__path-elapsed {
    stroke-width: 7px;
    stroke: rgba(255,255,255,0.2);
  }

  &__path-remaining {
    stroke-width: 7px;
    stroke-linecap: round;
    transform: rotate(90deg);
    transform-origin: center;
    transition: 1s linear all;
    fill-rule: nonzero;
    stroke: currentColor;

    &.green {
      color: #fff;
    }

    &.orange {
      color: orange;
    }

    &.red {
      color: red;
    }
  }

  &__label {
    position: absolute;
    width: 54px;
    height: 54px;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    opacity: 0;
  }

  &__times-up {
    display: inline-block;
    padding: 4px 16px;
    border-radius: 25px;
    background-color: var(--secondary-color);
    color: #fff;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) scale(.2);
    opacity: 0;
    animation: pop cubic-bezier(0.175, 0.885, 0.320, 1.275) .5s;
    animation-iteration-count: 1;
    transform-origin: 50% 50%;
    animation-fill-mode: forwards;
    white-space: nowrap;
    opacity: 0;
  }

  &__thank-you {
    display: inline-block;
    padding: 4px 16px;
    border-radius: 25px;
    background-color: var(--success-color);
    color: var(--white);
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) scale(.2);
    opacity: 0;
    animation: pop cubic-bezier(0.175, 0.885, 0.320, 1.275) .5s;
    animation-iteration-count: 1;
    transform-origin: 50% 50%;
    animation-fill-mode: forwards;
    white-space: nowrap;
  }

  &.hidden {
    height: 0;
    margin-top: 0;
  }
}

.swpoll-thankyou-msg {
  display: flex;
  align-items: center;
  transition: .5s ease-out;
  animation: fadepop cubic-bezier(0.175, 0.885, 0.320, 1.275) .5s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;

  p {
    margin: 0;
    font-size: 1.1rem;
  }

  svg {
    margin: 0 auto;
    display: block;
    transition: all 700ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
    width: 20px;
    height: auto;
    margin-right: 10px;
    transform: scale(.4);
    opacity: 0;

    circle,
    polyline {
      stroke: var(--white);
    }
  }
}

.animate-thank-you {

  .swpoll-thankyou-msg {

    svg {
      opacity: 1;
      transform: scale(1);
    }
  }
}

.fontLoaded {

  .base-timer__label,
  .base-timer__times-up {
    opacity: 1;
  }
}

.style-bbl,
.style-veev {

  .base-timer {
    position: absolute;
    left: 0;
    top: 24px;
    bottom: 24px;
    width: 175px;
    height: auto;
    box-sizing: border-box;
    border-right: 1px solid rgba(255,255,255,0.2);
    margin-top: 0;
    padding: 16px 36px 0;

    @media screen and (max-width: 768px) {
      padding: 0 18px 0;
      bottom: auto;
      border-right: 0;
      top: 15px;
      width: 90px;
    }
  }

  .base-timer__label {
    font-size: 24px;
    top: 40px;
    width: 102px;

    @media screen and (max-width: 768px) {
      font-size: 15px;
      top: 0px;
      width: 52px;
    }
  }

  .base-timer__times-up {

    @media screen and (max-width: 768px) {
      width: calc(100% - 40px);
      padding: 5px;
      font-size: 10px;
      white-space: normal;
      margin-top: 25px;
      border-radius: 10px;
      text-align: center;
    }
  }
}
.style-covenant .base-timer {
  display: none !important;
}
</style>
